import { z } from 'zod';
/**
 * Vídeo de execução para correção (P2 do personal; estudo: "Conversa com envio de vídeo de
 * execução para correção"). O aluno filma o exercício da ficha e pergunta; o personal assiste,
 * marca o momento ("0:08") e responde; os dois seguem a conversa no mesmo vídeo. O arquivo é
 * cifrado e sai 30 dias depois da resposta; a conversa e a miniatura ficam.
 */
export const EXEC_VIDEO_MAX_BYTES = 25 * 1024 * 1024;
export const EXEC_VIDEO_MAX_SECONDS = 60;
/** Vídeos esperando resposta, por aluno, ao mesmo tempo. */
export const EXEC_VIDEO_OPEN_MAX = 3;
/** Dias que o arquivo fica depois da resposta do personal. */
export const EXEC_VIDEO_KEEP_DAYS = 30;
export const EXEC_VIDEO_THUMB_MAX_BYTES = 300 * 1024;
export const EXEC_VIDEO_MIMES = ['video/mp4', 'video/webm', 'video/quicktime'];
/** enviando: o arquivo ainda não chegou; aguardando: a vez é do personal; respondido: do aluno. */
export const EXEC_VIDEO_STATUSES = ['enviando', 'aguardando', 'respondido'];
const isoDateTime = z.string().datetime({ offset: true });
export const execVideoMessageSchema = z.object({
    id: z.uuid(),
    from: z.enum(['aluno', 'personal']),
    author: z.string(),
    text: z.string(),
    at: isoDateTime,
});
export const execVideoSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    /** Só do lado da clínica. */
    patientName: z.string().optional(),
    planId: z.uuid().nullable(),
    itemId: z.string(),
    exercise: z.string(),
    durationSec: z.number().int(),
    status: z.enum(EXEC_VIDEO_STATUSES),
    /** O arquivo ainda está guardado (sai 30 dias depois da resposta). */
    hasVideo: z.boolean(),
    hasThumb: z.boolean(),
    createdAt: isoDateTime,
    answeredAt: isoDateTime.nullable(),
    /** Quando o arquivo sai (só depois de respondido). */
    expiresAt: isoDateTime.nullable(),
    messages: z.array(execVideoMessageSchema),
});
export const createExecVideoSchema = z.object({
    planId: z.uuid(),
    itemId: z.string().min(1).max(40),
    durationSec: z.number().int().min(1).max(EXEC_VIDEO_MAX_SECONDS),
    note: z.string().trim().max(400),
    /** Um quadro do vídeo, em JPEG, para a lista e para depois que o arquivo sair. */
    thumb: z.object({ mime: z.literal('image/jpeg'), data: z.string().min(10).max(420_000) }),
});
export const replyExecVideoSchema = z.object({ text: z.string().trim().min(1).max(800) });
/**
 * Os momentos citados no texto ("0:08", "1:05"), em segundos, com a posição: o player pula
 * para eles quando a pessoa toca. Só até o tamanho do vídeo.
 */
export function videoMarks(text, durationSec) {
    const out = [];
    for (const match of text.matchAll(/\b(\d{1,2}):([0-5]\d)\b/g)) {
        const seconds = Number(match[1]) * 60 + Number(match[2]);
        if (seconds > durationSec)
            continue;
        out.push({ start: match.index, end: match.index + match[0].length, seconds });
    }
    return out;
}
/** "0:08" a partir de segundos. */
export const markOf = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
